/* global module, require */
const Style = require('./Style'),
	THREE = require('three'),
	bwFork = require('../geo/bwFork'),
	bwScrew = require('../geo/bwScrew'),
	img = require('../../res/planner-wire.png'),
	makeTopRail = require('../func/makeTopRail'),
	steel = require('../mat/steel'),
	steelWire = require('../mat/steelWire'),
	unit = require('../unit'),
	wirePole = require('../geo/wirePole');

const gWireOffset = unit.mm(25),
	gWireRadius = unit.mm(4),
	gWireSpace = unit.mm(100);

function WirePost(planner, args) {
	this.args = args;
	this.name = 'Wire Posts';
	this.image = img;
	this.planner = planner;
	Style.apply(this);
}
WirePost.prototype = new Style();

WirePost.prototype.getConfig = function() {
	return {
		installation: {
			name: 'Installation Type',
			options: {
				Inside: 'Inside',
				Outside: 'Outside',
				Coastal: 'Coastal',
			},
			info:
				'System can be used for internal, external and coastal. The posts are calculated that a centre line distance between posts can be up to a maximum of 1200mm.',
		},
		totalHeight: {
			name: 'Total Post Height',
			range: { min: 900, max: 1300 },
			suffix: 'mm',
			info:
				'Post height has a range of 900mm to 1300mm based on the finished floor level to the height of the top rail. Care must be taken when giving a height requirement. For building control purposes 1100mm is a minimum requirement and we recommend this as the standard height provided. Also, under building control regulations, the gap between wires should be that a 100mm⌀ sphere should not pass through. For detailing purposes this is what will be offered. If you require less or more wire kits please advise on the submission form.',
		},
		poleThickness: {
			name: 'Upright Post Detail',
			options: {
				'42.4⌀ x 2.0mm wall': 42.4,
				'48.3⌀ x 2.0mm wall': 48.3,
			},
			transform: parseFloat,
			info:
				'Available in both 42⌀ and 48⌀ stainless steel posts complete with base cover and post tops.',
		},
		maxWireLength: {
			name: 'Max Wire Length',
			range: { min: 1000, max: 1200 },
			suffix: 'mm',
			transform: parseFloat,
		},
		wireThickness: {
			name: 'Wire Thickness',
			options: { '4mm (7 x 7)': 4 },
			transform: parseFloat,
			info:
				'Each wire is 4.0mm in diameter and comes complete with a tensioner and forked terminal. We offer wire kits from 1.0mtr to 10.0mtrs in incremental increases of 1.0mtr. All end and change of directional posts will be drilled to take a suitable eyebolt in order to attach the wire terminal or forked terminal through a relevant eye fitting. All intermediate posts will be drilled a minimum of 6.0mm in order that the wire can pass freely through the post.',
		},
	};
};

WirePost.prototype.setOptions = function(options) {
	this.options = {
		installation: 'Inside',
		totalHeight: 1100,
		poleThickness: 42.4,
		maxWireLength: 1000,
		...options,
	};
};

WirePost.prototype.addPole = function(position) {
	const mesh = new THREE.Mesh(
		wirePole.getGeometry(
			unit.mm(this.options.poleThickness) / 2,
			unit.mm(this.options.totalHeight)
		),
		steel
	);
	mesh.position.copy(position);

	return mesh;
};

WirePost.prototype.connect = function(a, b, sectionIndex) {
	const maxWireLength = unit.mm(this.options.maxWireLength),
		poleThickness = unit.mm(this.options.poleThickness),
		totalHeight = unit.mm(this.options.totalHeight);

	const between = new THREE.Vector3().subVectors(b, a),
		group = new THREE.Group(),
		railHeight = wirePole.getRailHeight(totalHeight, poleThickness),
		sectionCount = Math.ceil(between.length() / maxWireLength),
		sectionVec = new THREE.Vector3().copy(between).divideScalar(sectionCount),
		sectionVecReversed = new THREE.Vector3()
			.copy(sectionVec)
			.multiplyScalar(-1),
		tubeHeight = wirePole.getTubeHeight(totalHeight, poleThickness),
		wires = Math.floor(tubeHeight / gWireSpace);
	var position = new THREE.Vector3().copy(a);

	if (sectionIndex === 1) group.add(this.addPole(b));
	group.add(this.addPole(position));

	for (var section = 0; section < sectionCount; section++) {
		const next = new THREE.Vector3().copy(position).add(sectionVec);

		var height = wirePole.getBaseHeight() + tubeHeight - gWireOffset;

		if (section < sectionCount - 1) {
			group.add(this.addPole(next));
		}

		for (var i = 0; i < wires; i++) {
			group.add(
				makeWire({
					start: position,
					between: sectionVec,
					radius: gWireRadius,
					height,
				})
			);

			group.add(
				makeFork({
					start: position,
					between: sectionVec,
					height,
				})
			);

			group.add(
				makeScrew({
					start: next,
					between: sectionVecReversed,
					height,
				})
			);

			height -= gWireSpace;
		}

		group.add(
			makeTopRail({
				start: position,
				between: sectionVec,
				radius: poleThickness / 2,
				height: railHeight,
			})
		);

		position = next;
	}

	group.add(this.planner.call('makeSectionLabel', a, b, sectionIndex - 1));
	this.group.add(group);
};

function makeWire({ start, between, radius, height }) {
	const geo = new THREE.CylinderBufferGeometry(
		radius,
		radius,
		between.length()
	);
	const mesh = new THREE.Mesh(geo, steelWire);

	mesh.rotation.y = Math.PI / 2 + Math.atan2(between.x, between.z);
	mesh.rotation.z = Math.PI / 2;

	const dir = new THREE.Vector3()
		.copy(between)
		.divideScalar(2)
		.add(start);
	mesh.position.x = dir.x;
	mesh.position.y = height;
	mesh.position.z = dir.z;

	return mesh;
}

function makeFork({ start, between, height }) {
	const mesh = new THREE.Mesh(bwFork, steel);

	mesh.rotation.y = Math.atan2(between.x, between.z);
	mesh.rotation.z = Math.PI / 2;

	mesh.position.x = start.x;
	mesh.position.y = height;
	mesh.position.z = start.z;

	return mesh;
}

function makeScrew({ start, between, height }) {
	const mesh = new THREE.Mesh(bwScrew, steel);

	mesh.rotation.y = Math.atan2(between.x, between.z);
	mesh.rotation.z = Math.PI / 2;

	mesh.position.x = start.x;
	mesh.position.y = height;
	mesh.position.z = start.z;

	return mesh;
}

module.exports = WirePost;
